import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { SectionHeader } from "components/ui/section-header"
import { UserAvatar } from "components/ui/avatar"
import type { WidgetProps } from "lib/widget-types"
import { ChevronRight } from "lucide-react"
import { getUsers, getUsersStatus } from "services/users"

interface TeamMember {
  id: string
  firstName?: string
  lastName?: string
  username: string
  displayName: string
  profilePhotoUrl?: string | null
  online: boolean
  subtitle?: string
}

function buildDisplayName(user: any): string {
  if (user?.first_name && user?.last_name) {
    return `${user.first_name} ${user.last_name}`
  }
  if (user?.first_name) return user.first_name
  return user?.username || "Utente"
}

export function TeamStatusWidget({ widget }: WidgetProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [members, setMembers] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const [usersResp, statusResp] = await Promise.all([
          getUsers(),
          getUsersStatus().catch(() => ({ data: [] })),
        ])
        const users = Array.isArray(usersResp.data) ? usersResp.data : []
        const statusList = Array.isArray(statusResp.data) ? statusResp.data : []

        const onlineById: Record<number, boolean> = {}
        statusList.forEach((s: any) => {
          onlineById[s.id] = s?.is_online === true || s?.status === "online"
        })

        const formatted: TeamMember[] = users.map((user: any) => ({
          id: String(user.id),
          firstName: user.first_name || undefined,
          lastName: user.last_name || undefined,
          username: user.username || "",
          displayName: buildDisplayName(user),
          profilePhotoUrl: user.profile_photo_url || null,
          online: onlineById[user.id] ?? false,
          subtitle: user.role || "",
        }))

        setMembers(formatted)
      } catch (error) {
        console.error("Error fetching team members:", error)
        setMembers([])
      } finally {
        setLoading(false)
      }
    }

    fetchTeamMembers()
  }, [])

  return (
    <div
      className="rounded-xl backdrop-blur-sm p-4 border hexa-card-shadow h-full flex flex-col"
      style={{
        backgroundColor: "var(--hexa-card-bg)",
        borderColor: "var(--hexa-border)",
      }}
    >
      <Link to="/team" className="cursor-pointer flex-shrink-0">
        <SectionHeader title={widget.title || "Stato Team"} />
      </Link>

      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hexa space-y-2">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <span className="text-sm" style={{ color: "var(--hexa-text-secondary)" }}>Caricamento...</span>
          </div>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div
              className="flex items-center justify-center mb-2.5"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                backgroundColor: "var(--hexa-empty-icon-bg, rgba(123, 63, 255, 0.1))",
              }}
            >
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: "var(--hexa-empty-icon-color, #7b3fff)", opacity: "var(--hexa-empty-icon-opacity, 0.7)" }}
              >
                <path d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
              </svg>
            </div>
            <span
              className="text-center"
              style={{ fontSize: 11, color: "var(--hexa-empty-text-color, rgba(255,255,255,0.4))" }}
            >
              Nessun membro del team
            </span>
          </div>
        ) : (
          members.map((member) => {
            const isHovered = hoveredId === member.id
            return (
              <Link
                key={member.id}
                to="/team"
                className="block w-full p-2.5 rounded-lg border transition-colors group"
                style={{
                  backgroundColor: isHovered ? "var(--hexa-sidebar-item-hover)" : "var(--hexa-card-bg)",
                  borderColor: "var(--hexa-border-subtle)",
                  opacity: isHovered ? 0.9 : 1,
                }}
                onMouseEnter={() => setHoveredId(member.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <div className="flex items-center gap-2.5">
                  <UserAvatar
                    firstName={member.firstName}
                    lastName={member.lastName}
                    username={member.username}
                    imageUrl={member.profilePhotoUrl || undefined}
                    size="md"
                    showOnlineIndicator
                    isOnline={member.online}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate" style={{ color: "var(--hexa-text-primary)" }}>
                      {member.displayName}
                    </div>
                    {member.subtitle && (
                      <div className="text-xs truncate mt-0.5" style={{ color: "var(--hexa-text-secondary)" }}>
                        {member.subtitle}
                      </div>
                    )}
                  </div>
                  <ChevronRight
                    className="w-3.5 h-3.5 transition-opacity flex-shrink-0"
                    style={{
                      color: "var(--hexa-purple)",
                      opacity: isHovered ? 1 : 0,
                    }}
                  />
                </div>
              </Link>
            )
          })
        )}
      </div>
    </div>
  )
}
